// SeatGrid — Reusable interactive seat picker.
// Pure visual; selection state managed by parent via onSelect callback.

import * as React from 'react';
import {
  SEAT_ROWS,
  OCCUPIED_SEATS,
  getSeatTier,
  type SeatCol,
} from '@/lib/booking-mock';

interface Props {
  selected: string | null;
  onSelect: (seatId: string | null) => void;
}

// Colors using dark palette tokens directly (no Tailwind classes for inline styles)
const COLORS = {
  premiumBg:     '#1E1508',
  premiumBorder: 'rgba(245,166,35,0.35)',
  exitBg:        '#071520',
  exitBorder:    'rgba(75,142,219,0.35)',
  standardBg:    '#1A2540',
  standardBorder:'rgba(255,255,255,0.07)',
  occupiedBg:    '#0F1628',
  occupiedBorder:'rgba(255,255,255,0.04)',
  selectedBg:    '#FF5A3C',
  selectedBorder:'#FF5A3C',
  hoveredBg:     'rgba(255,90,60,0.28)',
  hoveredBorder: '#FF5A3C',
};

export function SeatGrid({ selected, onSelect }: Props) {
  const [hovered, setHovered] = React.useState<string | null>(null);

  function getSeatStyle(seatId: string, row: number): React.CSSProperties {
    const occupied = OCCUPIED_SEATS.has(seatId);
    const isSelected = selected === seatId;
    const isHovered  = hovered === seatId && !occupied;
    const tier = getSeatTier(row);

    let bg: string;
    let borderColor: string;

    if (isSelected) {
      bg = COLORS.selectedBg; borderColor = COLORS.selectedBorder;
    } else if (occupied) {
      bg = COLORS.occupiedBg; borderColor = COLORS.occupiedBorder;
    } else if (isHovered) {
      bg = COLORS.hoveredBg; borderColor = COLORS.hoveredBorder;
    } else if (tier === 'premium') {
      bg = COLORS.premiumBg; borderColor = COLORS.premiumBorder;
    } else if (tier === 'exit') {
      bg = COLORS.exitBg; borderColor = COLORS.exitBorder;
    } else {
      bg = COLORS.standardBg; borderColor = COLORS.standardBorder;
    }

    return {
      width: 26, height: 21,
      borderRadius: 5,
      background: bg,
      border: `1.5px solid ${borderColor}`,
      cursor: occupied ? 'not-allowed' : 'pointer',
      flexShrink: 0,
      transition: 'background 0.1s ease, border-color 0.1s ease',
      opacity: occupied ? 0.4 : 1,
    };
  }

  return (
    <div
      className="bg-[#131C30] rounded-[20px] border border-white/[0.07]"
      style={{ padding: '24px 28px' }}
    >
      {/* Aircraft nose */}
      <div className="text-center mb-4">
        <span className="text-[11px] text-[#4E5E78] font-bold tracking-[2px]"
              style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}>
          ▲ FRONT OF AIRCRAFT
        </span>
      </div>

      {/* Column headers */}
      <div className="flex items-center gap-1 mb-2" style={{ paddingLeft: 32 }}>
        {['A','B','C'].map(c => (
          <div key={c} className="w-[26px] text-center text-[10px] font-bold text-[#4E5E78]"
               style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}>{c}</div>
        ))}
        <div className="w-6 text-center text-[9px] text-[#4E5E78]"
             style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}>aisle</div>
        {['D','E','F'].map(c => (
          <div key={c} className="w-[26px] text-center text-[10px] font-bold text-[#4E5E78]"
               style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}>{c}</div>
        ))}
      </div>

      {/* Rows */}
      <div>
        {Array.from({ length: SEAT_ROWS }, (_, ri) => {
          const row = ri + 1;

          return (
            <React.Fragment key={row}>
              {/* Section labels */}
              {row === 1 && (
                <SectionDivider
                  label="PREMIUM ECONOMY — ROWS 1–4"
                  color="#F5A623"
                  borderColor="rgba(245,166,35,0.25)"
                />
              )}
              {row === 5 && (
                <SectionDivider
                  label="ECONOMY — ROWS 5–28"
                  color="#4E5E78"
                  borderColor="rgba(255,255,255,0.07)"
                />
              )}
              {row === 13 && (
                <SectionDivider
                  label="EXIT ROWS — EXTRA LEGROOM"
                  color="#4B8EDB"
                  borderColor="rgba(75,142,219,0.25)"
                />
              )}
              {row === 15 && (
                <div className="h-px my-1" style={{ background: 'rgba(255,255,255,0.05)' }} />
              )}

              {/* Seat row */}
              <div className="flex items-center gap-1 mb-1">
                {/* Row number */}
                <div
                  className="w-6 text-right text-[10px] font-semibold text-[#4E5E78] mr-1 flex-shrink-0"
                  style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                >
                  {row}
                </div>

                {/* Left bank A B C */}
                {(['A','B','C'] as SeatCol[]).map(col => {
                  const seatId = `${row}${col}`;
                  const occupied = OCCUPIED_SEATS.has(seatId);
                  return (
                    <div
                      key={col}
                      style={getSeatStyle(seatId, row)}
                      onMouseEnter={() => !occupied && setHovered(seatId)}
                      onMouseLeave={() => setHovered(null)}
                      onClick={() => {
                        if (occupied) return;
                        onSelect(selected === seatId ? null : seatId);
                      }}
                    />
                  );
                })}

                {/* Aisle gap */}
                <div className="w-6" />

                {/* Right bank D E F */}
                {(['D','E','F'] as SeatCol[]).map(col => {
                  const seatId = `${row}${col}`;
                  const occupied = OCCUPIED_SEATS.has(seatId);
                  return (
                    <div
                      key={col}
                      style={getSeatStyle(seatId, row)}
                      onMouseEnter={() => !occupied && setHovered(seatId)}
                      onMouseLeave={() => setHovered(null)}
                      onClick={() => {
                        if (occupied) return;
                        onSelect(selected === seatId ? null : seatId);
                      }}
                    />
                  );
                })}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ─── SECTION DIVIDER ─────────────────────────────────────────────────────────
function SectionDivider({
  label,
  color,
  borderColor,
}: {
  label: string;
  color: string;
  borderColor: string;
}) {
  return (
    <div className="flex items-center gap-2 my-1.5">
      <div className="flex-1 h-px" style={{ background: borderColor }} />
      <span
        className="text-[10px] font-bold tracking-[1px] flex-shrink-0"
        style={{
          fontFamily: '"Inter Variable","Inter",system-ui,sans-serif',
          color,
        }}
      >
        {label}
      </span>
      <div className="flex-1 h-px" style={{ background: borderColor }} />
    </div>
  );
}
